import { Box, Text, TextProps } from '@chakra-ui/react';
import { useMemo } from 'react';

export interface TypographyProps extends TextProps {
  modifiers?: string | string[] | Record<string, boolean>;
}

function applyModifiers(
  modifiers?: string | string[] | Record<string, boolean>,
) {
  if (!modifiers) return {};

  const styleProps: Record<string, any> = {};
  const classNames: string[] = [];

  // Convert modifiers to array format
  const modifierArray =
    typeof modifiers === 'string'
      ? [modifiers]
      : Array.isArray(modifiers)
        ? modifiers
        : Object.keys(modifiers).filter((key) => modifiers[key]);

  // Apply styles based on modifiers
  modifierArray.forEach((mod) => {
    classNames.push(`bundle-text--${mod}`);

    switch (mod) {
      case 'display':
      case 'heading':
      case 'subheading':
      case 'body':
      case 'caption':
        styleProps.textStyle = mod;
        break;
      case 'subdued':
        styleProps.color = 'text.subdued';
        break;
      case 'destructive':
        styleProps.color = 'text.destructive';
        break;
      case 'emphasis':
        styleProps.fontWeight = 'emphasized';
        break;
      case 'bold':
        styleProps.fontWeight = 'bold';
        break;
      case 'strikethrough':
        styleProps.textDecoration = 'line-through';
        break;
    }
  });

  if (classNames.length) {
    styleProps.className = classNames.join(' ');
  }

  return styleProps;
}

function Paragraph({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="p" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function Span({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="span" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function Div({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Box {...styleProps} {...rest}>
      {children}
    </Box>
  );
}

function H1({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="h1" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function H2({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="h2" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function H3({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="h3" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function H4({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="h4" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

function H5({ modifiers, children, ...rest }: TypographyProps) {
  const styleProps = useMemo(() => applyModifiers(modifiers), [modifiers]);

  return (
    <Text as="h5" {...styleProps} {...rest}>
      {children}
    </Text>
  );
}

export { Paragraph, Span, Div, H1, H2, H3, H4, H5 };
